import L from 'leaflet';

/**
 * Shared utility for rendering contest tasks on Leaflet maps
 * Used by both ContestTaskManager (for editing) and ContestTaskLeaderboardMap (for display)
 */
export class ContestTaskRenderer {
  constructor(map) {
    this.map = map;
    this.taskLayers = [];
  }

  /**
   * Clear all task layers from the map
   */
  clearTaskLayers() {
    this.taskLayers.forEach(layer => {
      if (this.map.hasLayer(layer)) {
        this.map.removeLayer(layer);
      }
    });
    this.taskLayers = [];
  }

  /**
   * Render a contest task from waypoints, markers, and handles
   * @param {Array} waypoints - Array of L.LatLng waypoint positions
   * @param {Array} markers - Array of marker objects with {marker, label, circle}
   * @param {Array} trackHandles - Array of route handles (optional)
   */
  renderFromWaypointsAndMarkers(waypoints, markers, trackHandles = []) {
    this.clearTaskLayers();

    if (!waypoints || waypoints.length === 0) {
      return;
    }

    // Render waypoint circles
    markers.forEach((markerObj) => {
      if (markerObj.circle) {
        const circle = L.circle(markerObj.circle.getLatLng(), {
          radius: markerObj.circle.getRadius(),
          color: '#1579a3',
          fill: true,
          fillColor: '#1579a3',
          fillOpacity: 0.3
        }).addTo(this.map);
        this.taskLayers.push(circle);
      }

      // Add marker
      const marker = L.marker(markerObj.marker.getLatLng(), {
        icon: markerObj.marker.options.icon,
        interactive: false
      }).addTo(this.map);
      this.taskLayers.push(marker);

      // Add label
      const label = L.marker(markerObj.label.getLatLng(), {
        icon: markerObj.label.getIcon(),
        interactive: false
      }).addTo(this.map);
      this.taskLayers.push(label);
    });

    // Render route lines if we have track handles
    if (trackHandles && trackHandles.length >= 2) {
      // Draw the main route line (center line - solid blue)
      const routePositions = trackHandles
        .filter(h => h.attachedWaypointIndex !== null)
        .map(h => h.getLatLng());

      if (routePositions.length >= 2) {
        const centerLine = L.polyline(routePositions, {
          color: '#005478',
          weight: 3
        }).addTo(this.map);
        this.taskLayers.push(centerLine);

        // Add direction arrows along the route
        const directionArrows = L.polylineDecorator(centerLine, {
          patterns: [{
            offset: 0,
            repeat: '200',
            symbol: L.Symbol.arrowHead({
              pixelSize: 15,
              polygon: false,
              pathOptions: { stroke: true, color: '#005478' }
            })
          }]
        }).addTo(this.map);
        this.taskLayers.push(directionArrows);
      }

      // Draw the optimized path (shortest path - dashed red line through tangent points)
      const optimizedPath = this.calculateOptimizedPath(waypoints, markers, trackHandles);
      if (optimizedPath.length >= 2) {
        const optimizedLine = L.polyline(optimizedPath, {
          color: 'red',
          weight: 3,
          dashArray: '5,5'
        }).addTo(this.map);
        this.taskLayers.push(optimizedLine);
      }
    }
  }

  /**
   * Calculate the optimized path through waypoint cylinders (tangent points)
   */
  calculateOptimizedPath(waypoints, markers, trackHandles) {
    const orderedWaypoints = [];

    trackHandles.forEach((handle) => {
      if (handle.attachedWaypointIndex !== null &&
          handle.attachedWaypointIndex < waypoints.length &&
          markers[handle.attachedWaypointIndex]) {
        orderedWaypoints.push({
          point: waypoints[handle.attachedWaypointIndex],
          radius: markers[handle.attachedWaypointIndex].circle?.getRadius() || 0
        });
      }
    });

    if (orderedWaypoints.length < 2) {
      return [];
    }

    const pathPoints = [];

    // First point (center of first waypoint)
    pathPoints.push(orderedWaypoints[0].point);

    // Intermediate points (tangent to circles)
    for (let i = 1; i < orderedWaypoints.length - 1; i++) {
      const prev = orderedWaypoints[i - 1];
      const curr = orderedWaypoints[i];
      const next = orderedWaypoints[i + 1];

      if (curr.radius === 0) {
        pathPoints.push(curr.point);
        continue;
      }

      const optimalPoint = this.findOptimalPointOnCircle(
        prev.point,
        curr.point,
        next.point,
        curr.radius
      );
      pathPoints.push(optimalPoint);
    }

    // Last point (center of last waypoint)
    pathPoints.push(orderedWaypoints[orderedWaypoints.length - 1].point);

    return pathPoints;
  }

  /**
   * Find the optimal tangent point on a circle
   */
  findOptimalPointOnCircle(prevPoint, circleCenter, nextPoint, radius) {
    const scale = Math.cos(circleCenter.lat * Math.PI / 180) * 111111;
    const x0 = (prevPoint.lng - circleCenter.lng) * scale;
    const y0 = (prevPoint.lat - circleCenter.lat) * 111111;
    const x1 = (nextPoint.lng - circleCenter.lng) * scale;
    const y1 = (nextPoint.lat - circleCenter.lat) * 111111;

    const anglePrev = Math.atan2(y0, x0);
    const angleNext = Math.atan2(y1, x1);

    let optimalAngle = (anglePrev + angleNext) / 2;
    if (Math.abs(angleNext - anglePrev) > Math.PI) {
      optimalAngle += Math.PI;
    }

    return L.latLng(
      circleCenter.lat + (Math.sin(optimalAngle) * radius) / 111111,
      circleCenter.lng + (Math.cos(optimalAngle) * radius) / scale
    );
  }

  /**
   * Render a contest task from API data (for leaderboard view)
   * @param {Number} contestTaskId - The contest task ID
   * @param {Function} callback - Callback when rendering is complete
   */
  async renderFromAPI(contestTaskId, callback) {
    try {
      const response = await fetch(`${window.application.getAPIUrl()}/v1/contest_task/${contestTaskId}`, {
        headers: {
          'X-STL-Token': cookies.get('STL-Token')
        }
      });

      if (!response.ok) {
        console.error('Failed to load contest task');
        return;
      }

      const data = await response.json();
      const { points } = data;

      this.clearTaskLayers();

      const bounds = L.latLngBounds([]);

      // Create temporary structures to mimic ContestTaskManager format
      const waypoints = [];
      const markers = [];
      const trackHandles = [];

      // Icons
      const waypointIcon = L.icon({
        iconUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fb/Map_pin_icon_green.svg/752px-Map_pin_icon_green.svg.png",
        iconRetinaUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fb/Map_pin_icon_green.svg/752px-Map_pin_icon_green.svg.png",
        iconSize: [25, 35],
        iconAnchor: [12, 35],
        popupAnchor: [1, -34]
      });

      const startIcon = L.icon({
        iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
        iconRetinaUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34]
      });

      const endIcon = L.icon({
        iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
        iconRetinaUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34]
      });

      // Filter route waypoints
      const routePoints = points.filter(point => point.is_route_waypoint !== false);

      // Add all waypoints
      points.forEach((point) => {
        const latlng = L.latLng(parseFloat(point.latitude), parseFloat(point.longitude));
        bounds.extend(latlng);
        waypoints.push(latlng);

        // Determine icon based on type
        let icon = waypointIcon;
        const typeMapping = {
          'TAKEOFF': startIcon,
          'GOAL': endIcon
        };
        if (typeMapping[point.type_id]) {
          icon = typeMapping[point.type_id];
        }

        // Create marker object
        const marker = L.marker(latlng, { icon });
        const labelIcon = L.divIcon({
          className: 'waypoint-label',
          html: `<div class="waypoint-text">${point.name}</div>`,
          iconSize: [40, 20],
          iconAnchor: [20, 40]
        });
        const label = L.marker(latlng, { icon: labelIcon });
        const circle = L.circle(latlng, { radius: point.radius });

        markers.push({ marker, label, circle });
      });

      // Create handles for route waypoints only
      routePoints.forEach((point) => {
        const waypointIndex = points.findIndex(p =>
          p.latitude === point.latitude &&
          p.longitude === point.longitude &&
          p.name === point.name
        );

        if (waypointIndex !== -1) {
          const handle = {
            attachedWaypointIndex: waypointIndex,
            getLatLng: () => waypoints[waypointIndex]
          };
          trackHandles.push(handle);
        }
      });

      // Render the task
      this.renderFromWaypointsAndMarkers(waypoints, markers, trackHandles);

      // Fit map bounds with better padding and zoom constraints
      if (bounds.isValid()) {
        // Add extra padding to ensure all markers and circles are visible
        const paddedBounds = bounds.pad(0.2); // Add 20% padding around the bounds

        this.map.fitBounds(paddedBounds, {
          padding: [30, 30],
          maxZoom: 14,
          animate: false
        });
      }

      if (callback) {
        callback();
      }

    } catch (error) {
      console.error('Error rendering contest task:', error);
    }
  }
}
